import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { ChatWindow, CloseIcon, IconButtonV2, toast } from '@utilities';
import {
  createChatMessage,
  createChatSession,
  readChatSession,
  readFlowState,
} from '@api/narrative-projects';

import {
  CHAT_RESPONSE_POLL_INTERVAL_MS,
  CHAT_RESPONSE_POLL_MAX_ATTEMPTS,
  DEFAULT_ASSISTANT_MESSAGE,
} from '../../utilities/constants';

const NarrativesChatWindow = ({
  allowCanvasHydrationOnMount = true,
  chatSessionId: providedChatSessionId = null,
  initialErrorMessage = '',
  initialInput = '',
  initialInputValue = '',
  initialMessages = [],
  onChatSessionReady,
  onCloseToolWindow,
  onFlowSelected,
  onFlowStateChange,
  onMessageSent,
  onStateSnapshot,
  shouldBreakAwayFromRunOnMessage = false,
  shouldHydrateOnMount = true,
  syncCanvasOnMount = true,
}) => {
  const [chatSessionId, setChatSessionId] = useState(providedChatSessionId);
  const [messages, setMessages] = useState(() => initialMessages || []);
  const [inputValue, setInputValue] = useState(initialInputValue || '');
  const [isInitializing, setIsInitializing] = useState(!initialMessages?.length);
  const [isAwaitingAssistant, setIsAwaitingAssistant] = useState(false);
  const [errorMessage, setErrorMessage] = useState(initialErrorMessage || '');

  const isMountedRef = useRef(true);
  const fallbackIdCounterRef = useRef(0);
  const emittedFlowIdRef = useRef(null);
  const emittedChatSessionRef = useRef(null);
  const hasSubmittedInitialInputRef = useRef(false);
  const hydratedProvidedSessionIdRef = useRef(null);
  const wasHydratedRef = useRef(Boolean(initialMessages?.length));
  const lastHydratedCanvasSnapshotRef = useRef(null);
  const assistantMessageCountRef = useRef(0);
  const pendingPollTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const createFallbackMessageId = useCallback((role) => {
    fallbackIdCounterRef.current += 1;
    return `${role}-message-${fallbackIdCounterRef.current}-${Date.now()}`;
  }, []);

  const wait = useCallback(
    (timeoutMs) =>
      new Promise((resolve) => {
        const timeoutId = window.setTimeout(() => {
          if (pendingPollTimeoutRef.current === timeoutId) {
            pendingPollTimeoutRef.current = null;
          }
          resolve();
        }, timeoutMs);

        pendingPollTimeoutRef.current = timeoutId;
      }),
    []
  );

  const buildCanvasSnapshot = useCallback((flowId, flowState) => {
    return JSON.stringify({
      flowId: flowId ?? null,
      flowState: flowState ?? null,
    });
  }, []);

  const hydrateMessages = useCallback(
    async (
      sessionId,
      { shouldBreakAwayFromRun = false, sourceOfChange = 'hydrate', syncCanvas = true } = {}
    ) => {
      if (!sessionId) return;

      try {
        const [messagesResult, canvasResult] = await Promise.allSettled([
          readChatSession({
            chatSessionId: sessionId,
            createFallbackId: createFallbackMessageId,
          }),
          syncCanvas
            ? readFlowState({
                chatSessionId: sessionId,
              })
            : Promise.resolve({ flowId: null, flowState: null }),
        ]);

        if (messagesResult.status !== 'fulfilled') {
          throw messagesResult.reason;
        }

        const { messages: nextMessages } = messagesResult.value;
        let nextCanvasState = { flowId: null, flowState: null };
        let didReadCanvasState = !syncCanvas;

        if (syncCanvas && canvasResult.status === 'fulfilled') {
          nextCanvasState = canvasResult.value;
          didReadCanvasState = true;
        } else if (syncCanvas && canvasResult.status === 'rejected') {
          console.error('Failed to read flow state', canvasResult.reason);
        }

        const nextFlowId = nextCanvasState?.flowId ?? null;
        const nextFlowState = nextCanvasState?.flowState ?? null;
        const hydratedMessages =
          initialInput && nextMessages.length === 0
            ? [
                ...nextMessages,
                {
                  content: initialInput,
                  id: createFallbackMessageId('user'),
                  role: 'user',
                },
              ]
            : nextMessages;

        if (isMountedRef.current) {
          wasHydratedRef.current = true;
          setMessages(hydratedMessages);
          setErrorMessage('');

          const nextCanvasSnapshot = buildCanvasSnapshot(nextFlowId, nextFlowState);
          const hasCanvasChanged = lastHydratedCanvasSnapshotRef.current !== nextCanvasSnapshot;

          lastHydratedCanvasSnapshotRef.current = nextCanvasSnapshot;

          if (syncCanvas && didReadCanvasState && hasCanvasChanged) {
            if (onFlowStateChange) {
              onFlowStateChange(nextFlowState || null, {
                shouldBreakAwayFromRun: Boolean(shouldBreakAwayFromRun),
                sourceOfChange: sourceOfChange,
              });
            }

            if (
              !shouldBreakAwayFromRun &&
              onFlowSelected &&
              nextFlowId &&
              nextFlowId !== emittedFlowIdRef.current
            ) {
              emittedFlowIdRef.current = nextFlowId;
              onFlowSelected(nextFlowId);
            }
          }
        }

        return {
          flowId: nextFlowId,
          flowState: nextFlowState,
          messages: hydratedMessages,
        };
      } catch (error) {
        console.error('Failed to read chat session', error);
        if (isMountedRef.current) {
          setErrorMessage('Unable to load your chat history. Please try again.');
        }
        throw error;
      }
    },
    [buildCanvasSnapshot, createFallbackMessageId, initialInput, onFlowSelected, onFlowStateChange]
  );

  const initializeSession = useCallback(async () => {
    if (providedChatSessionId) {
      setIsInitializing(false);
      return;
    }

    setIsInitializing(true);
    setErrorMessage('');

    try {
      const sessionId = await createChatSession();
      if (!sessionId) throw new Error('Chat service did not return a session identifier.');

      if (!isMountedRef.current) return;

      setChatSessionId(sessionId);
      emittedChatSessionRef.current = sessionId;
      if (onChatSessionReady) {
        onChatSessionReady(sessionId);
      }
      await hydrateMessages(sessionId);
    } catch (error) {
      console.error('Failed to initialize chat session', error);
      if (isMountedRef.current) {
        setErrorMessage('We could not start a chat session. Please refresh and try again.');
      }
    } finally {
      if (isMountedRef.current) setIsInitializing(false);
    }
  }, [hydrateMessages, onChatSessionReady, providedChatSessionId]);

  useEffect(() => {
    initializeSession();
  }, [initializeSession]);

  useEffect(() => {
    if (!providedChatSessionId) return;
    setChatSessionId(providedChatSessionId);
    emittedChatSessionRef.current = providedChatSessionId;
    if (onChatSessionReady) {
      onChatSessionReady(providedChatSessionId);
    }
    if (!shouldHydrateOnMount) {
      hydratedProvidedSessionIdRef.current = providedChatSessionId;
      wasHydratedRef.current = true;
      setIsInitializing(false);
      return;
    }
    if (hydratedProvidedSessionIdRef.current === providedChatSessionId) {
      setIsInitializing(false);
      return;
    }

    hydratedProvidedSessionIdRef.current = providedChatSessionId;
    setIsInitializing(true);
    hydrateMessages(providedChatSessionId, {
      syncCanvas: syncCanvasOnMount && allowCanvasHydrationOnMount,
    })
      .catch(() => {})
      .finally(() => {
        if (isMountedRef.current) setIsInitializing(false);
      });
  }, [
    allowCanvasHydrationOnMount,
    providedChatSessionId,
    hydrateMessages,
    onChatSessionReady,
    shouldHydrateOnMount,
    syncCanvasOnMount,
  ]);

  useEffect(() => {
    if (!chatSessionId || emittedChatSessionRef.current === chatSessionId) return;
    emittedChatSessionRef.current = chatSessionId;
    if (onChatSessionReady) {
      onChatSessionReady(chatSessionId);
    }
  }, [chatSessionId, onChatSessionReady]);

  const handleSubmit = useCallback(
    async (userInput) => {
      if (!chatSessionId) return;

      const trimmedInput = userInput?.trim();
      if (!trimmedInput) return;

      setIsAwaitingAssistant(true);
      setErrorMessage('');
      setInputValue('');

      try {
        await createChatMessage({ chatSessionId, userInput: trimmedInput });

        const existingAssistantMessageCount = assistantMessageCountRef.current;
        let attempts = 0;
        let hasCompletedAssistantReply = false;

        while (!hasCompletedAssistantReply && attempts < CHAT_RESPONSE_POLL_MAX_ATTEMPTS) {
          if (!isMountedRef.current) break;

          const hydratedSession = await hydrateMessages(chatSessionId, {
            shouldBreakAwayFromRun: shouldBreakAwayFromRunOnMessage,
            sourceOfChange: 'chat',
          });
          const hydratedMessages = Array.isArray(hydratedSession?.messages)
            ? hydratedSession.messages
            : [];
          const assistantMessages = hydratedMessages.filter(
            (message) => message?.role === 'assistant'
          );
          const hasNewAssistantReply = assistantMessages.length > existingAssistantMessageCount;
          const hasStreamingAssistantReply = assistantMessages.some((message) =>
            Boolean(message?.isStreaming)
          );

          hasCompletedAssistantReply = hasNewAssistantReply && !hasStreamingAssistantReply;
          if (hasCompletedAssistantReply) break;

          attempts += 1;
          if (attempts >= CHAT_RESPONSE_POLL_MAX_ATTEMPTS) break;
          await wait(CHAT_RESPONSE_POLL_INTERVAL_MS);
        }

        if (hasCompletedAssistantReply && onMessageSent) {
          onMessageSent();
        }
      } catch (error) {
        console.error('Failed to create chat message', error);
        if (isMountedRef.current) {
          toast.error('Unable to send your question. Please try again.');
          setInputValue(trimmedInput);
        }
      } finally {
        if (isMountedRef.current) setIsAwaitingAssistant(false);
      }
    },
    [chatSessionId, hydrateMessages, onMessageSent, shouldBreakAwayFromRunOnMessage, wait]
  );

  useEffect(() => {
    return () => {
      if (pendingPollTimeoutRef.current) {
        window.clearTimeout(pendingPollTimeoutRef.current);
        pendingPollTimeoutRef.current = null;
      }
    };
  }, []);

  const displayedMessages = useMemo(() => {
    if (messages.length) return messages;

    if (errorMessage) {
      return [{ content: errorMessage, id: 'assistant-error', role: 'assistant' }];
    }

    if (isInitializing) {
      return [
        {
          content: 'Setting up your Narratives chat...',
          id: 'assistant-initializing',
          role: 'assistant',
        },
      ];
    }

    return [DEFAULT_ASSISTANT_MESSAGE];
  }, [messages, errorMessage, isInitializing]);

  useEffect(() => {
    assistantMessageCountRef.current = messages.filter(
      (message) => message?.role === 'assistant'
    ).length;
  }, [messages]);

  const isInputDisabled =
    isInitializing || !chatSessionId || Boolean(errorMessage) || isAwaitingAssistant;

  useEffect(() => {
    if (!initialInput || !chatSessionId || messages.length > 0) return;
    if (hasSubmittedInitialInputRef.current) return;

    hasSubmittedInitialInputRef.current = true;

    handleSubmit(initialInput);
  }, [initialInput, chatSessionId, handleSubmit, messages.length]);

  useEffect(() => {
    if (!onStateSnapshot) return;
    onStateSnapshot({
      chatSessionId,
      errorMessage,
      inputValue,
      messages,
      wasHydrated: wasHydratedRef.current,
    });
  }, [chatSessionId, errorMessage, inputValue, messages, onStateSnapshot]);

  return (
    <ChatWindow
      headerAction={
        onCloseToolWindow ? (
          <IconButtonV2
            aria-label="Close"
            icon={<CloseIcon />}
            onClick={onCloseToolWindow}
            size="medium"
            tooltip="Close"
            tooltipProps={{ placement: 'left' }}
            variant="default"
          />
        ) : null
      }
      headerTitle="Numerator Nexa"
      inputDisabled={isInputDisabled}
      inputValue={inputValue}
      isAgentStreaming={isAwaitingAssistant}
      messages={displayedMessages}
      onChangeInput={setInputValue}
      onSubmit={handleSubmit}
      renderMarkdown="assistant"
      submitDisabled={isInputDisabled}
    />
  );
};

NarrativesChatWindow.propTypes = {
  allowCanvasHydrationOnMount: PropTypes.bool,
  chatSessionId: PropTypes.string,
  initialErrorMessage: PropTypes.string,
  initialInput: PropTypes.string,
  initialInputValue: PropTypes.string,
  initialMessages: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.string,
      id: PropTypes.string,
      role: PropTypes.string,
    })
  ),
  onChatSessionReady: PropTypes.func,
  onCloseToolWindow: PropTypes.func,
  onFlowSelected: PropTypes.func,
  onFlowStateChange: PropTypes.func,
  onMessageSent: PropTypes.func,
  onStateSnapshot: PropTypes.func,
  shouldBreakAwayFromRunOnMessage: PropTypes.bool,
  shouldHydrateOnMount: PropTypes.bool,
  syncCanvasOnMount: PropTypes.bool,
};

export default NarrativesChatWindow;
